import { useState } from 'react';
import { Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { BiTrash, BiErrorCircle, BiShow, BiCopyAlt } from 'react-icons/bi';
import useDeleteInvoice from '../../hooks/useDeleteInvoice';
import { formatDate, formatCurrency } from '../../utils/formatters';
import InvoiceDetailModal from './invoiceDetailModal';
import AccountingStatusPill from '../integrations/AccountingStatusPill';

// Matches FieldConfidenceFloor on ReceiptScanWorker (server). Revenue-side
// twin of expenseRow — see that file for the full rationale.
const CONFIDENCE_FLOOR = 0.85;
const isLowConf = (conf, key) => conf != null && conf[key] != null && conf[key] < CONFIDENCE_FLOOR;
const lowConfClass = (conf, key) => isLowConf(conf, key) ? 'text-warning fw-semibold' : '';

export default function InvoiceRow({ invoice, currency = 'GBP' }) {
    // Each invoice carries its own currency now; fall back to the job currency
    // prop for older payloads.
    const rowCurrency = invoice.currency || currency;
    const deleteInvoice = useDeleteInvoice();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    // Paid & locked in the accounting package → can't be deleted (server also
    // enforces this; the disabled button just avoids the error round-trip).
    const locked = invoice.accountingStatus === 'locked';

    const handleConfirmDelete = () => {
        // DeleteInvoice cascades on the server: the controller deletes each
        // attachment blob, then the DB FK cascade removes the rows alongside
        // the Invoice. No per-attachment UI needed here.
        deleteInvoice.mutate(invoice.id, {
            onSuccess: () => setConfirmOpen(false),
        });
    };

    const ocrConf = invoice.ocrFieldConfidence;
    const hasLowConf = ocrConf != null
        && Object.values(ocrConf).some(v => v != null && v < CONFIDENCE_FLOOR);

    const subtitle = [
        { text: formatDate(invoice.invoiceDate), key: 'invoiceDate' },
        { text: invoice.categoryName, key: 'category' },
        { text: invoice.customer, key: 'customer' },
    ].filter(part => part.text);

    return (
        <div className="surface p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1">
                    <div className="d-flex align-items-baseline gap-3 flex-wrap">
                        <strong className={lowConfClass(ocrConf, 'amount')}>
                            {formatCurrency(invoice.amount, rowCurrency)}
                        </strong>
                        {invoice.vatAmount != null && (
                            <span className={`text-muted small ${lowConfClass(ocrConf, 'vatAmount')}`}>
                                VAT {formatCurrency(invoice.vatAmount, rowCurrency)}
                            </span>
                        )}
                        {hasLowConf && (
                            <Badge color="warning" pill className="d-inline-flex align-items-center gap-1">
                                <BiErrorCircle /> Please check
                            </Badge>
                        )}
                        {invoice.possibleDuplicate && (
                            <Badge color="warning" pill className="d-inline-flex align-items-center gap-1"
                                   title="A similar invoice already exists. Open to review or dismiss.">
                                <BiCopyAlt /> Possible duplicate
                            </Badge>
                        )}
                        <AccountingStatusPill status={invoice.accountingStatus} />
                    </div>
                    {subtitle.length > 0 && (
                        <div className="text-muted small">
                            {subtitle.map((part, i) => (
                                <span key={i} className={part.key ? lowConfClass(ocrConf, part.key) : ''}>
                                    {i > 0 && ' · '}{part.text}
                                </span>
                            ))}
                        </div>
                    )}
                    {invoice.description && (
                        <div className="mt-1">{invoice.description}</div>
                    )}
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                    <Button color="primary" outline size="sm"
                            onClick={() => setDetailOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiShow /> View
                    </Button>
                    <Button color="danger" outline size="sm"
                            onClick={() => setConfirmOpen(true)}
                            disabled={locked}
                            title={locked ? 'Paid & locked in your accounting package — can’t be deleted' : 'Delete'}
                            className="d-inline-flex align-items-center gap-1">
                        <BiTrash />
                    </Button>
                </div>
            </div>

            <InvoiceDetailModal
                isOpen={detailOpen}
                onClose={() => setDetailOpen(false)}
                invoice={invoice}
                currency={rowCurrency}
            />

            <Modal isOpen={confirmOpen}
                   toggle={() => !deleteInvoice.isPending && setConfirmOpen(false)}
                   centered backdrop="static">
                <ModalHeader toggle={() => !deleteInvoice.isPending && setConfirmOpen(false)}>
                    Delete invoice?
                </ModalHeader>
                <ModalBody>
                    <p className="mb-2">
                        Permanently delete this invoice for {formatCurrency(invoice.amount, rowCurrency)}?
                    </p>
                    <p className="text-muted mb-0 small">
                        This also deletes the attached file. This cannot be undone.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setConfirmOpen(false)}
                            disabled={deleteInvoice.isPending}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleConfirmDelete}
                            disabled={deleteInvoice.isPending}>
                        {deleteInvoice.isPending && <Spinner size="sm" className="me-1" />}
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
